import { useState, useRef, useEffect } from "react"
import { motion, AnimatePresence } from "framer-motion"
import {
  SendIcon, User, Mail, Type, MessageSquare, LoaderIcon, Sparkles,
  ChevronLeft, ChevronRight, Users, Edit2, RotateCcw, Eye, Reply, 
  ChevronDown, ChevronUp, Calendar, X, PenTool, ArrowRight, UserCircle
} from "lucide-react"
import { cn } from "@/lib/utils"
import { EmailData } from "@/types"

// Email Composer State interface
interface EmailComposerState {
  to: string
  subject: string
  content: string
  recipients?: string[]
  currentIndex?: number
  mode?: 'single' | 'multi' | 'individual'
}

interface UnifiedEmailComposerProps {
  initialTo?: string | string[]
  initialSubject?: string
  initialContent?: string
  onSend: (email: { to: string; subject: string; content: string }) => Promise<void>
  onAIModify?: (instruction: string, currentEmail: { to: string; subject: string; content: string }) => Promise<{ to: string; subject: string; content: string }>
  onClose?: () => void
  className?: string
}

export function UnifiedEmailComposer({
  initialTo = "",
  initialSubject = "",
  initialContent = "",
  onSend,
  onAIModify,
  onClose,
  className
}: UnifiedEmailComposerProps) {
  // Parse recipients
  const toField = Array.isArray(initialTo) ? initialTo.join(", ") : initialTo
  const recipients = toField.split(',').map(r => r.trim()).filter(r => r)
  const isMultiRecipient = recipients.length > 1

  // State - Initialize immediately for single recipient
  const [mode, setMode] = useState<'single' | 'individual' | null>(
    isMultiRecipient ? null : 'single'
  )
  const [currentIndex, setCurrentIndex] = useState(0)
  const [emails, setEmails] = useState<EmailComposerState[]>(() => {
    // Initialize immediately for single recipient
    if (!isMultiRecipient) {
      return [{
        to: toField,
        subject: initialSubject || "",
        content: initialContent || ""
      }]
    }
    return []
  })
  const [isSending, setIsSending] = useState(false)
  const [showAIHelper, setShowAIHelper] = useState(false)
  const [aiInstruction, setAiInstruction] = useState("")
  const [isModifying, setIsModifying] = useState(false)

  // Sync internal state when initial props change
  useEffect(() => {
    const toFieldLocal = Array.isArray(initialTo) ? initialTo.join(", ") : (initialTo || "")
    const recipientsLocal = toFieldLocal.split(',').map(r => r.trim()).filter(r => r)
    const isMultiLocal = recipientsLocal.length > 1

    if (!isMultiLocal) {
      // Single mode: ensure mode and update the single email
      setMode('single')
      setEmails([{ to: toFieldLocal, subject: initialSubject || "", content: initialContent || "" }])
      setCurrentIndex(0)
    } else {
      // Individual mode: regenerate personalized emails
      const personalizedEmails = recipientsLocal.map(recipient => ({
        to: recipient,
        subject: initialSubject || "",
        content: (initialContent || "").trim() ? initialContent! : personalizeContent(initialContent || "", recipient)
      }))
      setEmails(personalizedEmails)
      setMode('individual')
      setCurrentIndex(prev => Math.min(prev, Math.max(0, personalizedEmails.length - 1)))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTo, initialSubject, initialContent])

  const personalizeContent = (content: string, recipientEmail: string): string => {
    const name = recipientEmail.split('@')[0]
      .split(/[._-]/)
      .map(part => part.charAt(0).toUpperCase() + part.slice(1))
      .join(' ')
    
    if (!content.trim()) return `Hi ${name},\n\n`
    
    const lines = content.split('\n')
    const firstLine = lines[0]?.trim() || ''
    const greetingKeywords = ['dear', 'hi', 'hello', 'hey', 'greetings']
    const hasGreeting = greetingKeywords.some(keyword => 
      firstLine.toLowerCase().startsWith(keyword)
    )
    
    if (hasGreeting) {
      lines[0] = `Hi ${name},`
      return lines.join('\n')
    }
    
    return `Hi ${name},\n\n${content}`
  }

  // Initialize emails based on mode
  const initializeEmails = (selectedMode: 'single' | 'individual') => {
    if (selectedMode === 'single') {
      setEmails([
        {
          to: toField,
          subject: initialSubject || "",
          content: initialContent || ""
        }
      ])
    } else {
      const personalizedEmails = recipients.map(recipient => ({
        to: recipient,
        subject: initialSubject || "",
        content: personalizeContent(initialContent || "", recipient)
      }))
      setEmails(personalizedEmails)
    }
    setMode(selectedMode)
  }

  const currentEmail = emails[currentIndex]
  
  const handleSend = async () => {
    if (!currentEmail || isSending) return
    setIsSending(true)
    try {
      await onSend(currentEmail)
      if (mode === 'individual' && emails.length > 1) {
        if (currentIndex < emails.length - 1) {
          setCurrentIndex(currentIndex + 1) // Move to next recipient
        } else {
          if (onClose) onClose() // Only close after last email
        }
      } else {
        if (onClose) onClose()
      }
    } catch (err) {
      console.error("Error sending email:", err)
    } finally {
      setIsSending(false)
    }
  }

  const handleSendAll = async () => {
    if (isSending) return
    setIsSending(true)
    try {
      for (const email of emails) {
        await onSend(email)
      }
      if (onClose) onClose()
    } catch (err) {
      console.error("Error sending emails:", err)
    } finally {
      setIsSending(false)
    }
  }

  const updateCurrentEmail = (updates: Partial<EmailComposerState>) => {
    setEmails(prev => prev.map((email, i) => 
      i === currentIndex ? { ...email, ...updates } : email
    ))
  }

  const handleAIModification = async () => {
    if (!aiInstruction.trim() || !onAIModify || !currentEmail) return
    setIsModifying(true)
    try {
      const modified = await onAIModify(aiInstruction, currentEmail)
      updateCurrentEmail(modified)
      setAiInstruction("")
      setShowAIHelper(false)
    } catch (err) {
      console.error("Error modifying email:", err)
    } finally {
      setIsModifying(false)
    }
  }

  const navigateEmail = (direction: 'prev' | 'next') => {
    if (direction === 'prev') {
      setCurrentIndex(prev => prev > 0 ? prev - 1 : emails.length - 1)
    } else {
      setCurrentIndex(prev => prev < emails.length - 1 ? prev + 1 : 0)
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      className={cn(
        "bg-[#1e293b]/95 backdrop-blur-xl border border-white/10 rounded-2xl p-4 shadow-2xl space-y-4",
        className
      )}
    >
      {/* Mode selection for multi-recipient - Inline */}
      {isMultiRecipient && mode === null && (
        <div className="space-y-4">
          {/* Header */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Users className="w-4 h-4 text-purple-400" />
              <div>
                <h3 className="text-white/90 font-medium text-sm">Multiple Recipients</h3>
                <p className="text-white/60 text-xs">
                  {recipients.length} recipients detected
                </p>
              </div>
            </div>
            {onClose && (
              <button
                onClick={onClose}
                className="p-1 text-white/40 hover:text-white/80 rounded-lg transition-colors"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>

          {/* Mode Selection - Compact inline buttons */}
          <div className="flex gap-2">
            <button
              onClick={() => initializeEmails('single')}
              className="flex-1 p-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg transition-colors group"
            >
              <div className="flex items-center gap-2">
                <Users className="w-3 h-3 text-blue-400 group-hover:text-blue-300" />
                <div className="text-left flex-1">
                  <div className="text-white/90 font-medium text-xs">One Email</div>
                  <div className="text-xs text-white/60">All see each other</div>
                </div>
                <ArrowRight className="w-3 h-3 text-white/40 ml-auto" />
              </div>
            </button>

            <button
              onClick={() => initializeEmails('individual')}
              className="flex-1 p-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg transition-colors group"
            >
              <div className="flex items-center gap-2">
                <User className="w-3 h-3 text-purple-400 group-hover:text-purple-300" />
                <div className="text-left flex-1">
                  <div className="text-white/90 font-medium text-xs">Individual</div>
                  <div className="text-xs text-white/60">Personalized</div>
                </div>
                <ArrowRight className="w-3 h-3 text-white/40 ml-auto" />
              </div>
            </button>
          </div>
        </div>
      )}

      {/* Main composer UI - Only show when mode is selected */}
      {currentEmail && mode && (
        <>
          {/* Header */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <PenTool className="w-4 h-4 text-blue-400" />
              <div>
                <h3 className="text-white/90 font-medium text-sm">
                  {emails.length > 1 ? `Individual Emails (${emails.length})` : 'Compose Email'}
                </h3>
                {emails.length > 1 && (
                  <p className="text-white/60 text-xs">
                    Email {currentIndex + 1} of {emails.length}
                  </p>
                )}
              </div>
            </div>
            
            <div className="flex items-center gap-1">
              {/* Navigation for multiple emails */}
              {emails.length > 1 && (
                <>
                  <button
                    onClick={() => navigateEmail('prev')}
                    className="p-1 text-white/40 hover:text-white/80 hover:bg-white/5 rounded-lg transition-colors"
                  >
                    <ChevronLeft className="w-3 h-3" />
                  </button>
                  <span className="text-xs text-white/60 min-w-[2.5rem] text-center">
                    {currentIndex + 1} / {emails.length}
                  </span>
                  <button
                    onClick={() => navigateEmail('next')}
                    className="p-1 text-white/40 hover:text-white/80 hover:bg-white/5 rounded-lg transition-colors"
                  >
                    <ChevronRight className="w-3 h-3" />
                  </button>
                </>
              )}
              
              {/* Close button */}
              {onClose && (
                <button
                  onClick={onClose}
                  className="p-1 text-white/40 hover:text-white/80 rounded-lg transition-colors ml-1"
                >
                  <X className="w-3 h-3" />
                </button>
              )}
            </div>
          </div>

          {/* Email Fields */}
          <div className="space-y-2">
            {/* To Field */}
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5 text-white/60 w-12 flex-shrink-0">
                <User className="w-3 h-3" />
                <span className="text-xs">To:</span>
              </div>
              <div className="flex-1 px-2 py-1.5 bg-white/5 border border-white/10 rounded-lg text-white/90 text-xs">
                {currentEmail.to}
              </div>
            </div>

            {/* Subject Field */}
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5 text-white/60 w-12 flex-shrink-0">
                <Type className="w-3 h-3" />
                <span className="text-xs">Subject:</span>
              </div>
              <input
                type="text"
                value={currentEmail.subject}
                onChange={e => updateCurrentEmail({ subject: e.target.value })}
                placeholder="Email subject"
                className="flex-1 bg-white/5 border border-white/10 rounded-lg px-2 py-1.5 text-white/90 placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/30 text-xs transition-all"
              />
            </div>

            {/* Message Field */}
            <div className="space-y-1">
              <div className="flex items-center gap-1.5 text-white/60">
                <MessageSquare className="w-3 h-3" />
                <span className="text-xs">Message:</span>
              </div>
              <textarea
                value={currentEmail.content}
                onChange={e => updateCurrentEmail({ content: e.target.value })}
                placeholder="Write your message here..."
                className="w-full h-32 bg-white/5 border border-white/10 rounded-lg p-3 text-white/90 placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/30 resize-none text-sm transition-all leading-relaxed"
              />
            </div>
          </div>

          {/* AI Helper */}
          <AnimatePresence>
            {showAIHelper && onAIModify && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2 }}
                className="bg-purple-500/10 border border-purple-500/20 rounded-lg p-3"
              >
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles className="w-3 h-3 text-purple-400" />
                  <span className="text-xs text-purple-300 font-medium">AI Assistant</span>
                </div>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={aiInstruction}
                    onChange={e => setAiInstruction(e.target.value)}
                    placeholder="How should I modify this email?"
                    className="flex-1 bg-white/5 border border-purple-500/30 rounded-lg px-2 py-1.5 text-white/90 placeholder:text-white/40 focus:outline-none focus:ring-2 focus:ring-purple-500/50 text-xs transition-all"
                    onKeyDown={e => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault()
                        handleAIModification()
                      }
                    }}
                  />
                  <button
                    onClick={handleAIModification}
                    disabled={!aiInstruction.trim() || isModifying}
                    className={cn(
                      "px-2 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 transition-all",
                      aiInstruction.trim() && !isModifying
                        ? "bg-purple-500/30 hover:bg-purple-500/40 text-purple-300"
                        : "bg-white/5 text-white/30 cursor-not-allowed"
                    )}
                  >
                    {isModifying ? <LoaderIcon className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                    {isModifying ? 'Modifying...' : 'Modify'}
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Actions */}
          <div className="flex items-center justify-between pt-2 border-t border-white/10">
            <div className="flex items-center gap-1">
              {onAIModify && (
                <button
                  onClick={() => setShowAIHelper(!showAIHelper)}
                  className={cn(
                    "px-2 py-1.5 rounded-lg text-xs flex items-center gap-1 transition-colors font-medium",
                    showAIHelper
                      ? "bg-purple-500/20 text-purple-300 hover:bg-purple-500/30"
                      : "bg-white/5 hover:bg-white/10 text-white/70"
                  )}
                >
                  <Sparkles className="w-3 h-3" />
                  AI Assist
                </button>
              )}
            </div>

            <div className="flex items-center gap-1">
              {/* Send All Button for multiple emails */}
              {emails.length > 1 && (
                <button
                  onClick={handleSendAll}
                  disabled={isSending}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 bg-purple-500/30 hover:bg-purple-500/40 text-purple-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSending ? <LoaderIcon className="w-3 h-3 animate-spin" /> : <SendIcon className="w-3 h-3" />}
                  Send All ({emails.length})
                </button>
              )}
              
              {/* Send Button */}
              <button
                onClick={handleSend}
                disabled={!currentEmail.to.trim() || !currentEmail.subject.trim() || !currentEmail.content.trim() || isSending}
                className={cn(
                  "px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1 transition-colors",
                  currentEmail.to.trim() && currentEmail.subject.trim() && currentEmail.content.trim() && !isSending
                    ? "bg-blue-500/30 hover:bg-blue-500/40 text-blue-300"
                    : "bg-white/5 text-white/30 cursor-not-allowed"
                )}
              >
                {isSending ? <LoaderIcon className="w-3 h-3 animate-spin" /> : <SendIcon className="w-3 h-3" />}
                {emails.length > 1 ? `Send #${currentIndex + 1}` : 'Send Email'}
              </button>
            </div>
          </div>
        </>
      )}
    </motion.div>
  )
}

// EmailViewer component for displaying emails
interface EmailViewerProps {
  email: EmailData
  onReply: (email: EmailData) => Promise<void> | void
  expanded?: boolean
  onToggleExpand?: () => void
  className?: string
}

export function EmailViewer({ 
  email, 
  onReply, 
  expanded = false, 
  onToggleExpand,
  className 
}: EmailViewerProps) {
  const [isGeneratingReply, setIsGeneratingReply] = useState(false)
  const fullBody = email.fullBody || email.snippet || ""
  const previewLength = 150
  const needsExpansion = fullBody.length > previewLength
  const displayBody = needsExpansion && !expanded 
    ? fullBody.substring(0, previewLength) + "..."
    : fullBody

  const handleReply = async () => {
    setIsGeneratingReply(true)
    try {
      await onReply(email)
    } catch (error) {
      console.error('Error generating reply:', error)
    } finally {
      setIsGeneratingReply(false)
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      className={cn(
        "bg-[#1e293b]/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl overflow-hidden",
        className
      )}
    >
      {/* Header */}
      <div className="p-4 border-b border-white/10">
        <div className="flex items-start justify-between mb-4">
          <h3 className="font-medium text-white/90 text-base leading-snug max-w-[70%]">
            {email.subject || "(No Subject)"}
          </h3>
          <button
            onClick={handleReply}
            disabled={isGeneratingReply}
            className={cn(
              "px-3 py-1.5 text-sm rounded-lg transition-colors flex items-center gap-1.5 font-medium",
              isGeneratingReply 
                ? "bg-blue-500/10 text-blue-500/50 cursor-not-allowed"
                : "bg-blue-500/20 hover:bg-blue-500/30 text-blue-400"
            )}
          >
            {isGeneratingReply ? (
              <>
                <LoaderIcon className="w-4 h-4 animate-spin" />
                Generating...
              </>
            ) : (
              <>
                <Reply className="w-4 h-4" />
                Reply
              </>
            )}
          </button>
        </div>
        
        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-3 text-white/70">
            <User className="w-4 h-4 text-white/50" />
            <span className="text-white/60 font-medium">From:</span>
            <span className="text-white/80">{email.from}</span>
          </div>
          
          {email.to && (
            <div className="flex items-center gap-3 text-white/70">
              <Mail className="w-4 h-4 text-white/50" />
              <span className="text-white/60 font-medium">To:</span>
              <span className="text-white/80">{email.to}</span>
            </div>
          )}

          {email.accountEmail && (
            <div className="flex items-center gap-3 text-white/70">
              <UserCircle className="w-4 h-4 text-blue-400" />
              <span className="text-white/60 font-medium">Account:</span>
              <span className="text-blue-400">{email.accountEmail}</span>
            </div>
          )}
          
          {email.date && (
            <div className="flex items-center gap-3 text-white/70">
              <Calendar className="w-4 h-4 text-white/50" />
              <span className="text-white/60 font-medium">Date:</span>
              <span className="text-white/80">{email.date}</span>
            </div>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="p-4">
        <div className="text-white/80 whitespace-pre-wrap text-sm leading-relaxed">
          {displayBody}
        </div>
        
        {needsExpansion && onToggleExpand && (
          <button
            onClick={onToggleExpand}
            className="mt-3 flex items-center gap-1 text-sm text-blue-400 hover:text-blue-300 transition-colors font-medium"
          >
            {expanded ? (
              <>
                <ChevronUp className="w-4 h-4" />
                Show less
              </>
            ) : (
              <>
                <ChevronDown className="w-4 h-4" />
                Show full email
              </>
            )}
          </button>
        )}
      </div>
    </motion.div>
  )
}